// entity.js

import { put, call, takeLatest } from "redux-saga/effects";

import { getWithStatusCheck } from "../helpers/fetch.js";

export const INITIAL_STATE = {
  isFetching: false,
  byId: {},
  allIds: [],
};

export const reduceById = (response, uniqueKey) => {
  return response.reduce((acc, cur) => {
    acc[cur[uniqueKey]] = cur;
    return acc;
  }, {});
};

export const mapAllIds = (response, uniqueKey) => {
  return response.map(cur => cur[uniqueKey]);
};

/**
 * Factory function that provides the necessary redux / redux-saga pieces for
 * managing state that we place in `state.entities.*`. This state typically
 * includes information we gather from the `trace.api` service.
 *
 * @param {string}   entity
 * @param {function} fetchActionCreator
 * @param {function} urlCreator
 * @param {string}   uniqueKey
 */
export const entityFactory = (
  entity,
  fetchActionCreator,
  urlCreator,
  uniqueKey = "id"
) => {
  // action types
  const FETCH = `trace-web/${entity}/FETCH`;
  const FETCH_SUCCESS = `trace-web/${entity}/FETCH_SUCCESS`;
  const FETCH_FAILURE = `trace-web/${entity}/FETCH_FAILURE`;

  // reducer
  const reducer = (state = INITIAL_STATE, action = {}) => {
    switch (action.type) {
      case FETCH:
        return {
          ...state,
          isFetching: true,
        };
      case FETCH_SUCCESS:
        return {
          ...state,
          isFetching: false,
          byId: reduceById(action[entity], uniqueKey),
          allIds: mapAllIds(action[entity], uniqueKey),
        };
      case FETCH_FAILURE:
        return {
          ...state,
          isFetching: false,
        };
      default:
        return state;
    }
  };

  // action creators
  const fetcher = fetchActionCreator(FETCH);

  const fetchSuccess = results => {
    return {
      type: FETCH_SUCCESS,
      [entity]: results[entity],
    };
  };

  const fetchFailure = error => {
    return {
      type: FETCH_FAILURE,
      error,
    };
  };

  // saga watcher
  function* watcher() {
    yield takeLatest(FETCH, worker);
  }

  // saga worker
  function* worker(action) {
    try {
      const url = urlCreator(action);
      const json = yield call(getWithStatusCheck, url);
      yield put(fetchSuccess(json));
    } catch (err) {
      yield put(fetchFailure(err));
    }
  }

  return {
    reducer,
    watcher,
    worker,
    fetcher,
    fetchSuccess,
    fetchFailure,
  };
};

// selectors
export const getIsFetching = (state, entity) =>
  state.entities[entity] && state.entities[entity].isFetching;
